import { useState, useEffect, useCallback } from 'react';
import {
  Plug, ToggleLeft, ToggleRight, Loader2, AlertCircle, Search,
  ShieldCheck, Phone, Brain, Webhook, HardDrive, Mail, Zap,
  ExternalLink, Filter, Clock, CheckCircle2, XCircle, CircleDot,
} from 'lucide-react';
import { api } from '@/utils/api';

interface Integration {
  key: string;
  name: string;
  description: string;
  provider: string;
  category: string;
  status: 'live' | 'planned' | 'pending';
  defaultEnabled: boolean;
  enabled: boolean;
}

interface UsageEntry {
  feature: string;
  _count: number;
  _sum: { creditsUsed: number | null };
}

const CATEGORY_ICONS: Record<string, any> = {
  'GST & Compliance': ShieldCheck,
  'Telephony': Phone,
  'AI Provider': Brain,
  'Lead Sources': Zap,
  'Ecosystem': Webhook,
  'Infrastructure': HardDrive,
};

const CATEGORY_COLORS: Record<string, string> = {
  'GST & Compliance': 'bg-red-100 text-red-700',
  'Telephony': 'bg-orange-100 text-orange-700',
  'AI Provider': 'bg-purple-100 text-purple-700',
  'Lead Sources': 'bg-yellow-100 text-yellow-700',
  'Ecosystem': 'bg-indigo-100 text-indigo-700',
  'Infrastructure': 'bg-teal-100 text-teal-700',
};

const STATUS_CONFIG = {
  live: { label: 'Live', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  planned: { label: 'Planned', color: 'bg-gray-100 text-gray-500', icon: CircleDot },
  pending: { label: 'Pending Setup', color: 'bg-amber-100 text-amber-700', icon: Clock },
};

export function APIIntegrationsPage() {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [usage, setUsage] = useState<UsageEntry[]>([]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [intRes, usageRes] = await Promise.all([
        api.get('/ai/integrations'),
        api.get('/credits/usage').catch(() => ({ data: { data: { features: [] } } })),
      ]);
      setIntegrations(intRes.data.data);
      setUsage(usageRes.data.data?.features || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load integrations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleIntegration = async (key: string, enabled: boolean) => {
    const integration = integrations.find(i => i.key === key);
    if (integration?.status === 'planned') return; // can't toggle planned
    setSaving(key);
    try {
      const res = await api.put('/ai/integrations', { toggles: { [key]: enabled } });
      setIntegrations(res.data.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update');
    } finally {
      setSaving(null);
    }
  };

  const categories = ['all', ...Array.from(new Set(integrations.map(a => a.category)))];

  const filtered = integrations.filter(a => {
    if (filterCategory !== 'all' && a.category !== filterCategory) return false;
    if (search) {
      const q = search.toLowerCase();
      return a.name.toLowerCase().includes(q) || a.description.toLowerCase().includes(q) || a.provider.toLowerCase().includes(q);
    }
    return true;
  });

  // Group by category
  const grouped = filtered.reduce<Record<string, Integration[]>>((acc, i) => {
    (acc[i.category] = acc[i.category] || []).push(i);
    return acc;
  }, {});

  const liveCount = integrations.filter(i => i.status === 'live' && i.enabled).length;
  const totalLive = integrations.filter(i => i.status === 'live').length;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-3 text-2xl font-bold text-gray-900">
            <Plug size={28} className="text-[var(--ink-0)]" />
            API Integrations
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            External APIs, compliance rules, and ecosystem connections. Toggle integrations on/off.
          </p>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-2">
            <span className="font-semibold text-green-700">{liveCount}</span>
            <span className="text-green-600"> / {totalLive} live & active</span>
          </div>
          <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-2">
            <span className="font-semibold text-gray-700">{integrations.length}</span>
            <span className="text-gray-500"> total integrations</span>
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          <AlertCircle size={16} />
          {error}
          <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">&times;</button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search integrations..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full rounded-lg border border-gray-300 pl-9 pr-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
          />
        </div>
        <div className="flex flex-wrap gap-1">
          {categories.map(c => (
            <button
              key={c}
              onClick={() => setFilterCategory(c)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                filterCategory === c
                  ? 'bg-[var(--ink-0)] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {c === 'all' ? 'All' : c}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-[var(--saffron-600)]" />
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(grouped).map(([category, items]) => {
            const CatIcon = CATEGORY_ICONS[category] || Plug;
            return (
              <div key={category}>
                <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
                  <CatIcon size={20} className="text-gray-500" />
                  {category}
                  <span className="text-xs font-normal text-gray-400">({items.length})</span>
                </h2>
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {items.map(item => (
                    <IntegrationCard key={item.key} item={item} saving={saving} usage={usage} onToggle={toggleIntegration} />
                  ))}
                </div>
              </div>
            );
          })}

          {filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <Filter size={40} className="mb-3 text-gray-300" />
              <p className="text-sm">No integrations match your search</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function IntegrationCard({ item, saving, usage, onToggle }: { item: Integration; saving: string | null; usage: UsageEntry[]; onToggle: (key: string, enabled: boolean) => void }) {
  const catColor = CATEGORY_COLORS[item.category] || 'bg-gray-100 text-gray-700';
  const statusCfg = STATUS_CONFIG[item.status];
  const StatusIcon = statusCfg.icon;
  const isSaving = saving === item.key;
  const isPlanned = item.status === 'planned';

  // Find usage for this integration key
  const usageEntry = usage.find((u: any) => u.feature === item.key);
  const usageCount = usageEntry ? (usageEntry as any)._count : 0;

  return (
    <div className={`rounded-xl border p-4 transition-all ${
      isPlanned ? 'border-dashed border-gray-300 bg-gray-50/50 opacity-60' :
      item.enabled ? 'border-green-200 bg-white shadow-sm' : 'border-gray-200 bg-gray-50 opacity-75'
    }`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-gray-900 truncate">{item.name}</h3>
          </div>
          <p className="mt-0.5 text-xs text-gray-500 line-clamp-2">{item.description}</p>
        </div>
        {!isPlanned && (
          <button
            onClick={() => onToggle(item.key, !item.enabled)}
            disabled={isSaving}
            className="flex-shrink-0"
          >
            {isSaving ? (
              <Loader2 size={28} className="animate-spin text-gray-400" />
            ) : item.enabled ? (
              <ToggleRight size={28} className="text-green-500 hover:text-green-600 transition-colors" />
            ) : (
              <ToggleLeft size={28} className="text-gray-400 hover:text-gray-500 transition-colors" />
            )}
          </button>
        )}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
          {item.provider}
        </span>
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${statusCfg.color}`}>
          <StatusIcon size={10} />
          {statusCfg.label}
        </span>
        {usageCount > 0 && (
          <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-600">
            {usageCount} calls this month
          </span>
        )}
        <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-semibold ${
          isPlanned ? 'bg-gray-100 text-gray-400' :
          item.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
        }`}>
          {isPlanned ? 'Coming Soon' : item.enabled ? 'Active' : 'Disabled'}
        </span>
      </div>
    </div>
  );
}
